/*!
  _   _  ___  ____  ___ ________  _   _   _   _ ___   ____  ____   ___
 | | | |/ _ \|  _ \|_ _|__  / _ \| \ | | | | | |_ _| |  _ \|  _ \ / _ \
 | |_| | | | | |_) || |  / / | | |  \| | | | | || |  | |_) | |_) | | | |
 |  _  | |_| |  _ < | | / /| |_| | |\  | | |_| || |  |  __/|  _ <| |_| |
 |_| |_|\___/|_| \_\___/____\___/|_| \_|  \___/|___| |_|   |_| \_\\___/

=========================================================
* Horizon UI Dashboard PRO - v1.0.0
=========================================================

* Product Page: https://www.horizon-ui.com/pro/
* Copyright 2022 Horizon UI (https://www.horizon-ui.com/)

* Designed and Coded by Simmmple

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import React from 'react';
import { Navigate, NavLink, useNavigate, useSearchParams } from 'react-router-dom';
// Chakra imports
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  useColorModeValue,
  Text,
  FormErrorMessage,
  Spinner,
} from '@chakra-ui/react';
// Assets
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
// Custom components
import CenteredAuth from 'layouts/auth/variants/Centered';

// Firebase config
import { signInWithEmailAndPassword, AuthError } from 'firebase/auth';
import { useAuth } from 'contexts/AuthContext';
import FirebaseAnalyticsService from 'services/firebaseAnalyticsService';
import { auth } from '../../../firebase/config';

function SignIn() {
  // Chakra color mode
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'gray.400';
  const textColorDetails = useColorModeValue('navy.700', 'secondaryGray.600');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectAfterLogin = searchParams.get('redirect');
  const [show, setShow] = React.useState(false);
  const [loginError, setLoginError] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [formFields, setFormFields] = React.useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = React.useState({
    email: '',
    password: '',
  });
  const { loading, user } = useAuth() || {};
  const { email, password } = formFields;

  const handleClick = () => setShow(!show);

  const handleSignIn = async (e: any) => {
    e.preventDefault();
    if (!email || !password) {
      setErrors({
        email: !email ? 'Email is required' : '',
        password: !password ? 'Password is required' : '',
      });
      return;
    }

    if (password.length < 8) {
      setErrors({
        ...errors,
        password: 'Password must be at least 8 characters',
      });
      return;
    }

    setIsLoading(true);
    setLoginError(null);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      FirebaseAnalyticsService.trackFeatureUsage('Auth', 'sign_in_success');
      navigate(
        redirectAfterLogin && redirectAfterLogin.startsWith('/')
          ? redirectAfterLogin
          : '/admin/home',
      );
    } catch (error) {
      // Firebase errors have a specific structure
      if (error && typeof error === 'object' && 'code' in error) {
        const firebaseError = error as AuthError;

        FirebaseAnalyticsService.trackFeatureUsage('Auth', 'sign_in_failed', {
          error_code: firebaseError.code,
        });

        // Handle specific Firebase error codes
        switch (firebaseError.code) {
          case 'auth/invalid-credential':
            setLoginError('Invalid email or password. Please try again.');
            break;
          case 'auth/user-not-found':
            setLoginError('No account found with this email address.');
            break;
          case 'auth/wrong-password':
            setLoginError('Incorrect password. Please try again.');
            break;
          case 'auth/too-many-requests':
            setLoginError('Too many failed attempts. Please try again later.');
            break;
          case 'auth/user-disabled':
            setLoginError('User is not approved. Please contact your admin.');
            break;
          default:
            setLoginError(
              firebaseError.message || 'An error occurred during sign in',
            );
        }
      } else {
        FirebaseAnalyticsService.trackFeatureUsage('Auth', 'sign_in_failed', {
          error_code: 'unknown',
        });
        setLoginError('An unknown error occurred during sign in');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setErrors({
      ...errors,
      [field]: '',
    });
    setFormFields({
      ...formFields,
      [field]: value,
    });
    // Clear login error when user starts typing
    if (loginError) {
      setLoginError(null);
    }
  };

  if (!loading && user) {
    return <Navigate to="/admin/home" />;
  }

  return (
    <form onSubmit={handleSignIn} noValidate>
      <CenteredAuth
        cardTop={{ base: '140px', md: '20vh' }}
        cardBottom={{ base: '50px', lg: 'auto' }}
        mx="0px"
      >
        <Flex
          maxW={{ base: '100%', md: 'max-content' }}
          w="100%"
          mx={{ base: 'auto', lg: '0px' }}
          me="auto"
          justifyContent="center"
          px={{ base: '20px', md: '0px' }}
          flexDirection="column"
        >
          <Box me="auto">
            <Heading color={textColor} fontSize="36px" mb="10px">
              Sign In
            </Heading>
            <Text
              mb="36px"
              ms="4px"
              color={textColorSecondary}
              fontWeight="400"
              fontSize="md"
            >
              Enter your email and password to sign in!
            </Text>
          </Box>
          {loginError && (
            <Box
              mb="24px"
              p="12px"
              bg="red.50"
              borderRadius="8px"
              border="1px solid"
              borderColor="red.200"
            >
              <Text color="red.600" fontSize="sm" fontWeight="500">
                {loginError}
              </Text>
            </Box>
          )}
          <Flex
            zIndex="2"
            direction="column"
            w={{ base: '100%', md: '420px' }}
            maxW="100%"
            background="transparent"
            borderRadius="15px"
            mx={{ base: 'auto', lg: 'unset' }}
            me="auto"
            mb={{ base: '20px', md: 'auto' }}
          >
            <FormControl mb="24px" isInvalid={!!errors.email}>
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                mb="8px"
              >
                Email<Text color={textColor}>*</Text>
              </FormLabel>
              <Input
                isRequired={true}
                isInvalid={!!errors.email}
                variant="auth"
                fontSize="sm"
                ms={{ base: '0px', md: '0px' }}
                type="email"
                placeholder="mail@simmmple.com"
                fontWeight="500"
                size="lg"
                onChange={({ target: { value } }) =>
                  handleChange('email', value)
                }
              />
              <FormErrorMessage ml="12px" mt="8px">
                {errors.email}
              </FormErrorMessage>
            </FormControl>
            <FormControl mb="24px" isInvalid={!!errors.password}>
              <FormLabel
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                display="flex"
              >
                Password<Text color={textColor}>*</Text>
              </FormLabel>
              <InputGroup size="md">
                <Input
                  isRequired={true}
                  isInvalid={!!errors.password}
                  fontSize="sm"
                  ms={{ base: '0px', md: '4px' }}
                  placeholder="Min. 8 characters"
                  size="lg"
                  type={show ? 'text' : 'password'}
                  variant="auth"
                  onChange={({ target: { value } }) =>
                    handleChange('password', value)
                  }
                />
                <InputRightElement display="flex" alignItems="center" mt="4px">
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: 'pointer' }}
                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={handleClick}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage mt="8px" ml="12px">
                {errors.password}
              </FormErrorMessage>
            </FormControl>
            <Flex justifyContent="flex-end" align="center" mb="24px">
              <NavLink to="/auth/forgot-password">
                <Text color="black" fontSize="sm" w="124px" fontWeight="700">
                  Forgot password?
                </Text>
              </NavLink>
            </Flex>
            <Button
              fontSize="sm"
              variant="brand"
              fontWeight="500"
              w="100%"
              h="50"
              mb="24px"
              type="submit"
              isLoading={isLoading}
              loadingText="Signing In..."
              disabled={isLoading}
            >
              {isLoading ? (
                <Flex align="center" gap="2">
                  <Spinner size="sm" color="white" />
                  Signing In...
                </Flex>
              ) : (
                'Sign In'
              )}
            </Button>

            <Flex
              flexDirection="column"
              justifyContent="center"
              alignItems="start"
              maxW="100%"
              mt="0px"
            >
              <Text color={textColorDetails} fontWeight="400" fontSize="14px">
                Not registered yet?
                <NavLink to="/auth/sign-up">
                  <Text color="black" as="span" ms="5px" fontWeight="700">
                    Create an Account
                  </Text>
                </NavLink>
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </CenteredAuth>
    </form>
  );
}

export default SignIn;
